import { inject } from '@angular/core';
import type { HttpEvent, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { HttpErrorResponse, HttpHeaders, HttpResponse } from '@angular/common/http';
import type { Observable } from 'rxjs';
import { from, lastValueFrom, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { createLogger } from '@hunting-nature/shared-core/logging';

// Configuration
import { getCacheConfig, getFreshForMs, getMaxAgeMs, isCacheableUrl } from '../config/cache.config';
import { CacheStrategy } from '../models/cache.models';
// Services
import { CacheService } from '../services/cache.service';
import { CacheUpdateNotifier } from '../services/cache-update-notifier.service';
import { CacheStateService } from '../services/cache-state.service';
import { BACKGROUND_REFRESH_CONTEXT_KEY } from '../utils/stale-while-revalidate.utils';

const log = createLogger('PostCacheInterceptor');

// --- Utility Functions ---

/**
 * Helper to create a SHA-256 hash of a string.
 */
async function hashString(input: string): Promise<string> {
  const messageBuffer = new TextEncoder().encode(input);
  const hashBuffer = await crypto.subtle.digest('SHA-256', messageBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Helper to convert browser Headers to Angular HttpHeaders.
 */
function convertHeadersToAngular(input: Headers | Record<string, string>): HttpHeaders {
  let output = new HttpHeaders();

  if (input instanceof Headers) {
    input.forEach((value, key) => {
      output = output.set(key, value);
    });
  } else {
    Object.entries(input).forEach(([key, value]) => {
      output = output.set(key, value);
    });
  }

  return output;
}

/**
 * Creates a unique cache key from a request.
 */
async function createCacheKey(request: HttpRequest<unknown>): Promise<Request> {
  const keyUrl = new URL(request.urlWithParams, location.origin);

  if (request.method === 'POST' && request.body) {
    const bodyText = typeof request.body === 'string' ? request.body : JSON.stringify(request.body);
    const bodyHash = await hashString(bodyText);
    keyUrl.searchParams.append('post-body-hash', bodyHash);
  }

  return new Request(keyUrl.toString(), { method: 'GET' });
}

/**
 * Convert Angular HttpHeaders to plain object
 */
function convertAngularHeadersToObject(headers: HttpHeaders): Record<string, string> {
  const result: Record<string, string> = {};
  for (const key of headers.keys()) {
    const value = headers.get(key);
    if (value) {
      result[key] = value;
    }
  }
  return result;
}

/**
 * Check if a request is a background refresh (triggered by a cache notification).
 *
 * Returns true if the request has the BACKGROUND_REFRESH_CONTEXT_KEY context
 * set, which indicates it was made by `createStaleWhileRevalidateStream` in
 * response to a cache update notification. The interceptor skips starting a
 * background revalidation for such requests to prevent the recursive loop.
 */
function isBackgroundRefreshRequest(request: HttpRequest<unknown>): boolean {
  return request.context.get(BACKGROUND_REFRESH_CONTEXT_KEY) === true;
}

/**
 * Asynchronously save response to cache
 */
async function saveToCacheAsync(
  cacheKey: Request,
  response: HttpResponse<unknown>,
  cacheService: CacheService,
  freshForMs: number,
  maxAgeMs: number
): Promise<void> {
  try {
    const headersObject: Record<string, string> = {};
    for (const key of response.headers.keys()) {
      headersObject[key] = response.headers.get(key) ?? '';
    }

    const responseToCache = Response.json(response.body, {
      status: response.status,
      statusText: response.statusText,
      headers: headersObject,
    });

    await cacheService.set(cacheKey, responseToCache, { freshForMs, maxAgeMs });
    log.debug('Successfully updated cache');
  } catch (error) {
    log.debug('Failed to save to cache', { error });
  }
}

/**
 * Try to get data from network
 * Returns the response for both success and HTTP errors (4xx/5xx)
 * Only returns undefined for actual network failures (offline, DNS error)
 */
async function tryNetwork(
  request: HttpRequest<unknown>,
  next: (request_: HttpRequest<unknown>) => Observable<HttpEvent<unknown>>
): Promise<HttpEvent<unknown> | undefined> {
  try {
    log.debug('Attempting network request', { url: request.url });

    const networkResponse = await lastValueFrom(
      next(request).pipe(
        catchError((error: unknown) => {
          // If it's an HttpErrorResponse with a status code, the network worked
          // but the server returned an error (404, 500, etc.)
          if (error instanceof HttpErrorResponse && error.status > 0) {
            log.debug('Server error response', { status: error.status, url: request.url });
            throw error; // Will be caught below and returned
          }

          log.debug('Network request failed', { url: request.url, error });
          throw error;
        })
      )
    );

    return networkResponse;
  } catch (error) {
    // If it's an HttpErrorResponse with a status, return it (server responded with error)
    if (error instanceof HttpErrorResponse && error.status > 0) {
      return error as unknown as HttpEvent<unknown>;
    }

    // True network failure (offline, DNS error) - return undefined
    return undefined;
  }
}

/**
 * Try to get data from cache
 */
async function tryCache(cacheKey: Request, cacheService: CacheService, requestUrl: string) {
  try {
    const cachedData = await cacheService.get(cacheKey);

    if (cachedData) {
      log.debug('Serving from cache', { url: requestUrl });

      const headersObject = convertAngularHeadersToObject(convertHeadersToAngular(cachedData.headers));
      headersObject['X-Cache-Source'] = 'cache';
      headersObject['X-Cache-Timestamp'] = cachedData.metadata.cachedAt;
      headersObject['X-Cache-Expired'] = cachedData.metadata.isExpired.toString();

      if (cachedData.metadata.isExpired) log.debug('Serving expired cache data', { url: requestUrl });

      return new HttpResponse({
        body: cachedData.body,
        status: cachedData.status,
        statusText: cachedData.statusText,
        headers: convertHeadersToAngular(headersObject),
      });
    }
  } catch (cacheError) {
    log.debug('Cache retrieval failed', { error: cacheError });
  }
  return;
}

function isSuccessfulResponse(response: HttpResponse<unknown>): boolean {
  return response.status >= 200 && response.status < 300;
}

interface CacheStorageParams {
  cacheKey: Request;
  cacheService: CacheService;
  freshForMs: number;
  maxAgeMs: number;
}

interface CacheRuntimeParams {
  cacheStateService: CacheStateService;
  cacheUpdateNotifier: CacheUpdateNotifier;
}

interface CacheParams {
  storage: CacheStorageParams;
  runtime: CacheRuntimeParams;
}

interface BackgroundRevalidationArguments {
  request: HttpRequest<unknown>;
  next: (request: HttpRequest<unknown>) => Observable<HttpEvent<unknown>>;
  cachedResponse: HttpResponse<unknown>;
  cacheParams: CacheParams;
}

function startBackgroundRevalidation({ request, next, cachedResponse, cacheParams }: BackgroundRevalidationArguments): void {
  const { cacheKey, cacheService, freshForMs, maxAgeMs } = cacheParams.storage;
  const { cacheStateService, cacheUpdateNotifier } = cacheParams.runtime;

  // Guard against recursive revalidations.
  // When a cache notification triggers a re-fetch through the interceptor,
  // the request is marked with BACKGROUND_REFRESH_CONTEXT_KEY context. Skip
  // starting another background revalidation for such requests to prevent the
  // infinite loop.
  if (isBackgroundRefreshRequest(request)) {
    log.debug('Skipping background revalidation for background refresh', { url: request.url });
    return;
  }

  // Kick off a background refresh intentionally without awaiting.
  tryNetwork(request, next)
    .then(networkResponse => {
      if (!(networkResponse instanceof HttpResponse) || !networkResponse.ok) {
        return;
      }

      cacheStateService.setDataSource('network');

      const cachedBody = JSON.stringify(cachedResponse.body);
      const freshBody = JSON.stringify(networkResponse.body);
      const dataChanged = cachedBody !== freshBody;

      saveToCacheAsync(cacheKey, networkResponse, cacheService, freshForMs, maxAgeMs).catch(error => {
        log.debug('Background refresh failed to save', { error });
      });

      if (dataChanged) {
        log.debug('SWR data changed, notifying subscribers', { url: request.url });
        cacheUpdateNotifier.notify(request.url);
      }

      log.debug('Background revalidation complete', { url: request.url });
    })
    .catch((error: unknown) => {
      log.debug('Background refresh error', { error });
    });
}

interface StaleWhileRevalidateArguments {
  request: HttpRequest<unknown>;
  next: (request: HttpRequest<unknown>) => Observable<HttpEvent<unknown>>;
  cacheParams: CacheParams;
}

async function tryServeStaleWhileRevalidate({ request, next, cacheParams }: StaleWhileRevalidateArguments): Promise<HttpResponse<unknown> | undefined> {
  const { cacheKey, cacheService } = cacheParams.storage;
  const { cacheStateService } = cacheParams.runtime;
  const cachedResponse = await tryCache(cacheKey, cacheService, request.url);

  if (!cachedResponse || !isSuccessfulResponse(cachedResponse)) {
    log.debug('StaleWhileRevalidate: cold cache, waiting for network', { url: request.url });
    return undefined;
  }

  cacheStateService.setDataSource('cache');
  startBackgroundRevalidation({
    request,
    next,
    cachedResponse,
    cacheParams,
  });

  return cachedResponse;
}

function handleSuccessfulNetworkResponse(
  request: HttpRequest<unknown>,
  networkResponse: HttpResponse<unknown>,
  cacheParams: CacheParams
): HttpResponse<unknown> {
  const { cacheKey, cacheService, freshForMs, maxAgeMs } = cacheParams.storage;
  const { cacheStateService } = cacheParams.runtime;

  log.debug('Network success, updating cache', { url: request.url });
  cacheStateService.setDataSource('network');

  saveToCacheAsync(cacheKey, networkResponse, cacheService, freshForMs, maxAgeMs).catch(error => {
    log.debug('Failed to update cache (non-critical)', { error });
  });

  return networkResponse;
}

function handleServerErrorResponse(request: HttpRequest<unknown>, networkResponse: HttpErrorResponse, cacheParams: CacheParams): never {
  const { cacheKey, cacheService } = cacheParams.storage;
  const { cacheStateService } = cacheParams.runtime;

  log.debug('Server error response', { status: networkResponse.status, url: request.url });

  cacheService.delete(cacheKey).catch(error => {
    log.debug('Failed to delete bad cache entry', { error });
  });

  cacheStateService.setDataSource('network');
  throw networkResponse;
}

async function tryServeNetworkFailureCacheFallback(request: HttpRequest<unknown>, cacheParams: CacheParams): Promise<HttpResponse<unknown> | undefined> {
  const { cacheKey, cacheService } = cacheParams.storage;
  const { cacheStateService } = cacheParams.runtime;

  log.debug('True network failure, checking cache fallback');

  const cachedResponse = await tryCache(cacheKey, cacheService, request.url);
  if (!cachedResponse) {
    return undefined;
  }

  if (isSuccessfulResponse(cachedResponse)) {
    cacheStateService.setDataSource('cache');
    return cachedResponse;
  }

  log.warn('Cached error response found, deleting', { status: cachedResponse.status });
  await cacheService.delete(cacheKey);
  return undefined;
}

function createOfflineError(url: string): HttpErrorResponse {
  return new HttpErrorResponse({
    error: {
      message: 'Unable to load data. Please check your connection and try again.',
      offline: true,
    },
    status: 0,
    statusText: 'Offline',
    url,
  });
}

/**
 * Enhanced cache handling with network-first strategy.
 */
async function handleCaching(
  request: HttpRequest<unknown>,
  next: (request_: HttpRequest<unknown>) => Observable<HttpEvent<unknown>>,
  cacheService: CacheService,
  cacheStateService: CacheStateService,
  cacheUpdateNotifier: CacheUpdateNotifier
): Promise<HttpEvent<unknown>> {
  // Build a deterministic cache key (includes POST body hash when relevant).
  const cacheKey = await createCacheKey(request);

  // Resolve strategy and per-endpoint cache TTL settings.
  const config = getCacheConfig(request.url);
  const freshForMs = getFreshForMs(request.url);
  const maxAgeMs = getMaxAgeMs(request.url);
  const strategy = config?.strategy ?? CacheStrategy.NetworkFirst;
  const cacheParams: CacheParams = {
    storage: {
      cacheKey,
      cacheService,
      freshForMs,
      maxAgeMs,
    },
    runtime: {
      cacheStateService,
      cacheUpdateNotifier,
    },
  };

  // Stale While Revalidate: return cached data immediately when available and refresh in background.
  if (strategy === CacheStrategy.StaleWhileRevalidate) {
    const staleResponse = await tryServeStaleWhileRevalidate({
      request,
      next,
      cacheParams,
    });

    if (staleResponse) {
      return staleResponse;
    }
  }

  // Network-first path (also used after SWR cold-cache fallback).
  const networkResponse = await tryNetwork(request, next);

  // Successful network response: mark source and persist fresh payload in cache.
  if (networkResponse instanceof HttpResponse && networkResponse.ok) {
    return handleSuccessfulNetworkResponse(request, networkResponse, cacheParams);
  }

  // Server returned an HTTP error: invalidate stale cache entry and rethrow.
  if (networkResponse instanceof HttpErrorResponse && networkResponse.status > 0) {
    return handleServerErrorResponse(request, networkResponse, cacheParams);
  }

  // True network failure: try serving a valid cached response as offline fallback.
  const fallbackResponse = await tryServeNetworkFailureCacheFallback(request, cacheParams);
  if (fallbackResponse) {
    return fallbackResponse;
  }

  // Nothing usable from network or cache: expose a consistent offline error.
  log.debug('No valid cache available', { url: request.url });
  cacheStateService.setDataSource('unknown');

  throw createOfflineError(request.url);
}

/**
 * Enhanced POST Cache Interceptor
 */
export function postCacheInterceptor(
  request: HttpRequest<unknown>,
  next: (request: HttpRequest<unknown>) => Observable<HttpEvent<unknown>>
): Observable<HttpEvent<unknown>> {
  const cacheService = inject(CacheService);
  const cacheStateService = inject(CacheStateService);
  const cacheUpdateNotifier = inject(CacheUpdateNotifier);

  const isGetOrPost = request.method === 'GET' || request.method === 'POST';
  const isCacheable = isCacheableUrl(request.url);

  if (!isGetOrPost || !isCacheable) {
    return next(request);
  }

  log.debug('Intercepting cacheable request', { method: request.method, url: request.url });

  return from(handleCaching(request, next, cacheService, cacheStateService, cacheUpdateNotifier)).pipe(
    catchError((error: unknown) => {
      // Convert thrown errors (from async handleCaching) into observable errors.
      // This prevents unhandled promise rejections and lets Angular's HTTP
      // error handling and NgRx effect catchError work correctly.
      return throwError(() => error);
    })
  );
}
